// src/pages/Inventory/StockItemsManager.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Plus, Edit3, Trash2, Loader, Save, X, RefreshCw, Upload } from 'lucide-react';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import { useSearchParams } from 'react-router-dom';
import api from '@/lib/axios'; // ✅ unified client (adds /solo-mdsh-api base)

const API = '/inventory/stock-items'; // ✅ relative to VITE_API_BASE_URL

/* -------------------- Import helpers -------------------- */
const FIELD_ALIASES = {
  item_code: ['item_code', 'code', 'sku', 'item code', 'itemcode', 'product code'],
  description: ['description', 'desc', 'name', 'item name', 'product name', 'title'],
  unit_size: ['unit_size', 'size', 'unit size', 'pack size', 'uom'],
  unit_price: ['unit_price', 'price', 'unit price', 'cost', 'unit cost'],
  par_level: ['par_level', 'par', 'min level', 'par level', 'min qty'],
  reorder_frequency: ['reorder_frequency', 'reorder freq', 'reorder days', 'frequency', 'reorder_frequency (days)'],
  current_stock: ['current_stock', 'stock', 'qty', 'quantity', 'on hand', 'onhand'],
  category: ['category', 'group', 'department'],
  department_id: ['department_id', 'dept', 'department id', 'dept id'],
  vendor_id: ['vendor_id', 'supplier_id'],
  vendor_name: ['vendor_name', 'vendor', 'supplier', 'supplier_name'],
  // ✅ NEW: allow forcing each in import sheets
  force_each: ['force_each', 'force each', 'forceeach', 'as each', 'treat as each'],
};

const CANON = Object.keys(FIELD_ALIASES);

const normalizeHeader = (h) =>
  String(h || '').trim().toLowerCase().replace(/\s+/g, ' ').replace(/[^a-z0-9 _-]/g, '');

const autoMapHeaders = (headers) => {
  const norm = headers.map(normalizeHeader);
  const map = {};
  for (const c of CANON) {
    const aliases = FIELD_ALIASES[c].map(normalizeHeader);
    const idx = norm.findIndex((h) => aliases.includes(h));
    if (idx !== -1) map[c] = headers[idx];
  }
  return map;
};

const extractRows = (rows, mapping) =>
  rows.map((r) => {
    const out = {};
    for (const c of CANON) {
      const src = mapping[c];
      if (src) out[c] = r[src] ?? null;
    }
    return out;
  });

/** Robust numeric parser for CSV/XLS(X) values */
const parseNumeric = (v) => {
  if (v === null || v === undefined) return null;
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  const s = String(v)
    .trim()
    .replace(/[\u00A0\s]/g, '') // non-breaking & normal spaces
    .replace(/[£$€]/g, '') // currency symbols
    .replace(/,/g, '') // thousands separators
    .replace(/^\((.*)\)$/, '-$1'); // (123.45) -> -123.45
  if (s === '' || s.toLowerCase() === 'na' || s.toLowerCase() === 'n/a' || s === '—') return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};

/** Parse boolean-ish values from CSV (true/false/1/0/yes/no) */
const parseBool = (v) => {
  if (v === null || v === undefined) return false;
  if (typeof v === 'boolean') return v;
  const s = String(v).trim().toLowerCase();
  return s === 'true' || s === '1' || s === 'yes' || s === 'y';
};

/** Apply numeric coercion to known numeric fields */
const coerceNumbers = (row) => {
  const out = { ...row };
  ['unit_price', 'par_level', 'reorder_frequency', 'current_stock', 'department_id', 'vendor_id'].forEach((k) => {
    out[k] = parseNumeric(out[k]);
  });
  if (out.force_each !== undefined && out.force_each !== null) out.force_each = parseBool(out.force_each);
  return out;
};

const ImportItemsModal = ({ open, onClose, onDone, vendorIdDefault }) => {
  const [rawHeaders, setRawHeaders] = useState([]);
  const [mapping, setMapping] = useState({});
  const [previewRows, setPreviewRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [applyVendorId, setApplyVendorId] = useState(vendorIdDefault || '');
  const [fileName, setFileName] = useState('');

  // 🔎 vendor_name → vendor_id resolver (loaded when modal opens)
  const [vendorMapByName, setVendorMapByName] = useState(new Map());
  const norm = (s) => String(s || '').trim().toLowerCase().replace(/\s+/g, ' ');
  const buildVendorMap = (arr) => {
    const m = new Map();
    for (const v of Array.isArray(arr) ? arr : []) {
      const id = v.vendor_id ?? v.id;
      const name = v.vendor_name ?? v.name;
      if (id && name) m.set(norm(name), Number(id));
    }
    return m;
  };
  const loadVendors = async () => {
    try {
      const tries = [() => api.get('/vendor-directory'), () => api.get('/vendors', { params: { status: 'active' } })];
      for (const t of tries) {
        try {
          const r = await t();
          const list =
            Array.isArray(r.data)
              ? r.data
              : Array.isArray(r.data?.vendors)
              ? r.data.vendors
              : Array.isArray(r.data?.rows)
              ? r.data.rows
              : [];
          const map = buildVendorMap(list);
          if (map.size) {
            setVendorMapByName(map);
            return;
          }
        } catch {
          /* next */
        }
      }
      setVendorMapByName(new Map());
    } catch {
      setVendorMapByName(new Map());
    }
  };
  useEffect(() => {
    if (open) loadVendors();
  }, [open]);

  // Close on ESC
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const resetImportState = () => {
    setRawHeaders([]);
    setMapping({});
    setPreviewRows([]);
    setApplyVendorId(vendorIdDefault || '');
    setFileName('');
  };

  const readFile = async (file) => {
    const name = file.name.toLowerCase();
    if (name.endsWith('.csv')) {
      return new Promise((resolve, reject) => {
        Papa.parse(file, {
          header: true,
          skipEmptyLines: 'greedy',
          complete: (res) => resolve(res.data),
          error: reject,
        });
      });
    } else if (name.endsWith('.xlsx') || name.endsWith('.xls')) {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      return XLSX.utils.sheet_to_json(ws, { defval: '' });
    }
    throw new Error('Unsupported file type. Use CSV or XLS/XLSX.');
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const rows = await readFile(f);
      const headers = rows.length ? Object.keys(rows[0]) : [];
      setRawHeaders(headers);
      const auto = autoMapHeaders(headers);
      setMapping(auto);
      const extracted = extractRows(rows, auto).map(coerceNumbers);
      setPreviewRows(extracted.slice(0, 200)); // ✅ keep view snappy
      setFileName(f.name);
      toast.success(`Loaded ${rows.length} rows. Review mapping then Preview/Commit.`);
    } catch (err) {
      toast.error(err.message || 'Failed to read file');
    } finally {
      setBusy(false);
      // allow picking same file again if needed
      e.target.value = '';
    }
  };

  const resolveVendors = (rows) => {
    // 1) use vendor_name to fill vendor_id if missing
    let unresolved = 0;
    const out = rows.map((r) => {
      const row = { ...r };
      if (!row.vendor_id && row.vendor_name) {
        const id = vendorMapByName.get(norm(row.vendor_name));
        if (id) row.vendor_id = id;
        else unresolved++;
      }
      return row;
    });
    if (unresolved > 0) {
      toast.info(
        `${unresolved} row(s) have vendor_name not found. They will import without a link unless you set "Apply vendor to all rows".`
      );
    }
    return out;
  };

  const sendPreview = async () => {
    if (!previewRows.length) return toast.error('Please upload a file first.');
    setBusy(true);
    try {
      let rows = previewRows.map((r) => ({ ...r }));
      rows = resolveVendors(rows);
      if (applyVendorId) rows = rows.map((r) => (r.vendor_id ? r : { ...r, vendor_id: Number(applyVendorId) }));
      await api.post(`${API}/bulk/preview`, { items: rows });
      toast.success('Preview OK. You can Commit now.');
    } catch (e) {
      const errs = e?.response?.data?.errors;
      if (Array.isArray(errs) && errs.length) {
        const sample = errs
          .slice(0, 3)
          .map((er) => `Row ${er.index + 1}${er.item_code ? ` (${er.item_code})` : ''}: ${er.message || er.error}`)
          .join('\n');
        toast.error(`Preview failed:\n${sample}${errs.length > 3 ? `\n…+${errs.length - 3} more` : ''}`);
      } else {
        toast.error(e?.response?.data?.message || e?.response?.data?.error || 'Preview failed');
      }
    } finally {
      setBusy(false);
    }
  };

  const sendCommit = async () => {
    if (!previewRows.length) return toast.error('Please upload a file first.');
    setBusy(true);
    const t = toast.loading('Importing…');
    try {
      let rows = previewRows.map((r) => ({ ...r }));
      rows = resolveVendors(rows);
      if (applyVendorId) rows = rows.map((r) => (r.vendor_id ? r : { ...r, vendor_id: Number(applyVendorId) }));
      const { data } = await api.post(`${API}/bulk/commit`, { items: rows });
      toast.dismiss(t);
      toast.success(`Import complete: ${data.results?.length || 0} rows processed.`);
      onDone?.();
      resetImportState();
      onClose?.();
    } catch (e) {
      toast.dismiss(t);
      const errs = e?.response?.data?.errors;
      if (Array.isArray(errs) && errs.length) {
        const sample = errs
          .slice(0, 3)
          .map((er) => `Row ${er.index + 1}${er.item_code ? ` (${er.item_code})` : ''}: ${er.message || er.error}`)
          .join('\n');
        toast.error(`Some rows failed:\n${sample}${errs.length > 3 ? `\n…+${errs.length - 3} more` : ''}`);
      } else {
        toast.error(e?.response?.data?.message || e?.response?.data?.error || 'Commit failed');
      }
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div className="bg-[#0f0f0f] border border-gray-800 rounded-xl w-full max-w-5xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="px-4 py-3 border-b border-gray-800 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold">Import Items (CSV/XLS/XLSX)</h2>
            {fileName && <span className="text-xs text-gray-400">• {fileName}</span>}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={resetImportState}
              className="text-gray-400 hover:text-gray-200 text-sm px-2 py-1 rounded border border-gray-700"
            >
              Reset
            </button>
            <button onClick={onClose} className="text-gray-400 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Scrollable Body */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          <div className="flex items-center gap-3">
            <input
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={onFile}
              className="text-sm text-gray-300 file:mr-3 file:px-3 file:py-1.5 file:rounded file:border file:border-gray-700 file:bg-[#161616]"
            />
            <div className="text-xs text-gray-400">
              Required: <span className="text-gray-200">item_code</span>, <span className="text-gray-200">description</span>.&nbsp;
              Optional: unit_size, unit_price, par_level, reorder_frequency, current_stock, category, department_id, vendor_id / vendor_name,{' '}
              <span className="text-gray-200">force_each</span>.
            </div>
          </div>

          {rawHeaders.length > 0 && (
            <div className="rounded-lg border border-gray-800 p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-semibold">Header Mapping</div>
                <div className="text-xs text-gray-400">
                  {Object.values(mapping).filter(Boolean).length} / {CANON.length} mapped
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                {CANON.map((c) => (
                  <label key={c} className="flex flex-col gap-1">
                    <span className="text-xs text-gray-400">{c}</span>
                    <select
                      value={mapping[c] || ''}
                      onChange={(e) => setMapping((m) => ({ ...m, [c]: e.target.value || undefined }))}
                      className="bg-[#161616] border border-gray-700 rounded-lg px-3 py-2 outline-none"
                    >
                      <option value="">— Not Mapped —</option>
                      {rawHeaders.map((h) => (
                        <option key={h} value={h}>
                          {h}
                        </option>
                      ))}
                    </select>
                  </label>
                ))}
              </div>
              <div className="text-xs text-gray-400 mt-2">Only mapped columns are imported. Column order doesn’t matter.</div>
              <div className="text-xs text-gray-500 mt-2">
                unit_size examples: 1kg, 250g, 2L, 500ml, 6x2L, 24x500ml, 6x700g, 20pcs, 10each, 1x10 (→ 10 each). For non-measurable items
                (pack/roll/set), set force_each=true.
              </div>
            </div>
          )}

          <div className="flex items-center gap-3">
            <label className="text-sm text-gray-300">Apply vendor to all rows (optional):</label>
            <input
              type="number"
              placeholder="vendor_id"
              value={applyVendorId}
              onChange={(e) => setApplyVendorId(e.target.value)}
              className="w-36 bg-[#161616] border border-gray-700 rounded px-2 py-1.5 outline-none"
            />
            <span className="text-xs text-gray-500">(Leave empty if your file already has vendor_id/vendor_name.)</span>
          </div>

          {previewRows.length > 0 && (
            <div className="rounded-lg border border-gray-800 overflow-hidden">
              <div className="max-h-[50vh] overflow-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-[#121212] text-gray-300 sticky top-0 z-10">
                    <tr>
                      {CANON.filter((c) => mapping[c]).map((c) => (
                        <th key={c} className="px-3 py-2 text-left">
                          {c}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-[#0f0f0f]">
                    {previewRows.map((r, i) => (
                      <tr key={i} className="border-t border-gray-800">
                        {CANON.filter((c) => mapping[c]).map((c) => (
                          <td key={c} className="px-3 py-2">
                            {String(r[c] ?? '')}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="px-3 py-2 text-xs text-gray-400 border-t border-gray-800">Showing first 200 rows for preview.</div>
            </div>
          )}
        </div>

        {/* Sticky Footer */}
        <div className="px-4 py-3 border-t border-gray-800 flex justify-end gap-2 bg-[#0f0f0f] sticky bottom-0">
          <button
            disabled={busy}
            onClick={sendPreview}
            className="px-3 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-60"
          >
            {busy ? 'Working…' : 'Preview'}
          </button>
          <button
            disabled={busy}
            onClick={sendCommit}
            className="px-3 py-2 bg-emerald-600 hover:bg-emerald-700 rounded-lg disabled:opacity-60"
          >
            {busy ? 'Importing…' : 'Commit Import'}
          </button>
        </div>
      </div>
    </div>
  );
};

/* -------------------- Main page -------------------- */

const emptyForm = {
  item_code: '',
  description: '',
  unit_size: '',
  unit_price: '',
  par_level: '',
  reorder_frequency: '',
  current_stock: '',
  category: '',
  department_id: '',
  // ✅ NEW
  force_each: false,
};

export default function StockItemsManager() {
  const [params] = useSearchParams();
  // ✅ accept either ?vendor or ?vendorId
  const vendorIdFromURL = params.get('vendor') || params.get('vendorId') || '';

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState('');

  const robustArray = (data) =>
    Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : Array.isArray(data?.rows) ? data.rows : [];

  const load = async () => {
    setLoading(true);
    try {
      let url = API;
      let q = { limit: 500, sort: 'item_code', dir: 'asc' };

      // ✅ Vendor context → only that vendor’s items
      if (vendorIdFromURL) {
        url = `${API}/by-vendor/${encodeURIComponent(vendorIdFromURL)}`;
        q = {};
      }

      const { data } = await api.get(url, { params: q });
      setRows(robustArray(data));
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.error || 'Failed to load stock items');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [vendorIdFromURL]);

  const startCreate = () => {
    setForm(emptyForm);
    setEditingId(null);
    setShowForm(true);
  };

  const startEdit = (row) => {
    setForm({
      item_code: row.item_code ?? '',
      description: row.description ?? '',
      unit_size: row.unit_size ?? '',
      unit_price: row.unit_price ?? '',
      par_level: row.par_level ?? '',
      reorder_frequency: row.reorder_frequency ?? '',
      current_stock: row.current_stock ?? '',
      category: row.category ?? '',
      department_id: row.department_id ?? '',
      // default false; user can enable if needed
      force_each: false,
    });
    setEditingId(row.item_id);
    setShowForm(true);
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    const t = toast.loading(editingId ? 'Saving changes…' : 'Creating item…');
    try {
      const payload = scrub(form);
      if (editingId) {
        await api.patch(`${API}/${editingId}`, payload);
        toast.dismiss(t);
        toast.success('Item updated');
        // 🧩 Ensure link exists when editing under vendor context
        if (vendorIdFromURL) {
          try {
            await api.post(`${API}/${editingId}/vendors`, { vendor_id: Number(vendorIdFromURL) });
          } catch {}
        }
      } else {
        const { data } = await api.post(API, payload);
        const created = data?.item || data; // tolerate either shape
        // Link to vendor when in context
        if (vendorIdFromURL && created?.item_id != null) {
          try {
            await api.post(`${API}/${created.item_id}/vendors`, { vendor_id: Number(vendorIdFromURL) });
          } catch (linkErr) {
            console.error(linkErr);
          }
        }
        toast.dismiss(t);
        toast.success('Item created');
      }
      setShowForm(false);
      setEditingId(null);
      setForm(emptyForm);
      await load();
    } catch (e2) {
      console.error(e2);
      toast.dismiss(t);
      // ✅ surface backend validation message if present
      const msg =
        e2?.response?.data?.message ||
        e2?.response?.data?.error ||
        'Save failed';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!confirm(`Delete ${row.item_code || row.description || 'this item'}?`)) return;
    const t = toast.loading('Deleting…');
    try {
      await api.delete(`${API}/${row.item_id}`);
      setRows((prev) => prev.filter((r) => r.item_id !== row.item_id));
      toast.dismiss(t);
      toast.success('Deleted');
    } catch (e) {
      console.error(e);
      toast.dismiss(t);
      toast.error(e?.response?.data?.error || 'Delete failed');
    }
  };

  const filtered = useMemo(() => {
    const q = (search || '').toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [r.item_code, r.description, r.category, r.unit_size]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [rows, search]);

  return (
    <div className="min-h-screen px-6 md:px-12 py-8 bg-gradient-to-br from-[#050505] via-[#0f0f0f] to-[#0a0a0a] text-white">
      <div className="flex items-end justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold">Stock Items</h1>
          <p className="text-gray-400 text-sm">Create, edit, delete, or bulk import items used across POs & inventory.</p>
          {vendorIdFromURL && (
            <p className="text-xs text-emerald-400 mt-1">
              Vendor context: <span className="font-semibold">#{vendorIdFromURL}</span> — newly created / imported rows will be linked.
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <button
            onClick={load}
            className="bg-[#161616] hover:bg-[#1f1f1f] px-3 py-2 rounded-lg border border-gray-700 flex items-center gap-2"
          >
            {loading ? <Loader className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Refresh
          </button>
          <button
            onClick={() => setShowImport(true)}
            className="bg-[#161616] hover:bg-[#1f1f1f] px-3 py-2 rounded-lg border border-gray-700 flex items-center gap-2"
          >
            <Upload className="w-4 h-4" /> Import CSV/XLSX
          </button>
          <button
            onClick={startCreate}
            className="bg-emerald-600 hover:bg-emerald-700 px-3 py-2 rounded-lg flex items-center gap-2"
          >
            <Plus className="w-4 h-4" /> New Item
          </button>
        </div>
      </div>

      <div className="mb-4 flex items-center gap-3">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search item code, description, category…"
          className="w-full md:w-96 bg-[#161616] border border-gray-700 rounded-lg px-3 py-2 outline-none"
        />
      </div>

      <div className="overflow-x-auto rounded-xl border border-gray-800">
        <table className="min-w-full text-sm">
          <thead className="bg-[#121212] text-gray-300">
            <tr>
              <Th>Code</Th>
              <Th>Description</Th>
              <Th>Unit Size</Th>
              <Th className="text-right">Unit Price</Th>
              <Th className="text-right">Par</Th>
              <Th className="text-right">Reorder Freq</Th>
              <Th className="text-right">Stock</Th>
              <Th>Category</Th>
              <Th>Dept</Th>
              <Th>Actions</Th>
            </tr>
          </thead>
          <tbody className="bg-[#0f0f0f]">
            {loading ? (
              <tr>
                <td colSpan={10} className="px-3 py-6 text-center text-gray-400">
                  <Loader className="inline w-4 h-4 animate-spin" /> Loading…
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={10} className="px-3 py-6 text-center text-gray-400">
                  No items.
                </td>
              </tr>
            ) : (
              filtered.map((r) => (
                <tr key={r.item_id} className="border-t border-gray-800">
                  <Td>{r.item_code}</Td>
                  <Td>{r.description}</Td>
                  <Td>{r.unit_size ?? '—'}</Td>
                  <Td className="text-right">{fmt(r.unit_price)}</Td>
                  <Td className="text-right">{r.par_level ?? '—'}</Td>
                  <Td className="text-right">{r.reorder_frequency ?? '—'}</Td>
                  <Td className="text-right">{r.current_stock ?? 0}</Td>
                  <Td>{r.category ?? '—'}</Td>
                  <Td>{r.department_id ?? '—'}</Td>
                  <Td>
                    <div className="flex gap-2">
                      <button
                        onClick={() => startEdit(r)}
                        className="bg-blue-600 hover:bg-blue-700 px-2 py-1 rounded flex items-center gap-1"
                      >
                        <Edit3 className="w-4 h-4" /> Edit
                      </button>
                      <button
                        onClick={() => remove(r)}
                        className="bg-rose-600 hover:bg-rose-700 px-2 py-1 rounded flex items-center gap-1"
                      >
                        <Trash2 className="w-4 h-4" /> Delete
                      </button>
                    </div>
                  </Td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create/Edit */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-[#0f0f0f] border border-gray-800 rounded-xl w-full max-w-3xl">
            <div className="px-4 py-3 border-b border-gray-800 flex items-center justify-between">
              <h2 className="text-lg font-semibold">{editingId ? 'Edit Item' : 'New Item'}</h2>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={submit} className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
              <Input
                label="Item Code"
                value={form.item_code}
                onChange={(v) => setForm((f) => ({ ...f, item_code: v }))}
                required
              />
              <Input
                label="Description"
                value={form.description}
                onChange={(v) => setForm((f) => ({ ...f, description: v }))}
                required
              />

              <div className="md:col-span-2">
                <Input
                  label="Unit Size"
                  value={form.unit_size}
                  onChange={(v) => setForm((f) => ({ ...f, unit_size: v }))}
                />
                <div className="text-xs text-gray-500 mt-1">
                  Accepted examples: <span className="text-gray-300">1kg</span>, <span className="text-gray-300">250g</span>,{' '}
                  <span className="text-gray-300">2L</span>, <span className="text-gray-300">500ml</span>,{' '}
                  <span className="text-gray-300">6x2L</span>, <span className="text-gray-300">24x500ml</span>,{' '}
                  <span className="text-gray-300">6x700g</span>, <span className="text-gray-300">20pcs</span>,{' '}
                  <span className="text-gray-300">10each</span>, <span className="text-gray-300">1x10</span> (→ 10 each).
                </div>

                <label className="mt-3 flex items-center gap-2 text-sm text-gray-300">
                  <input
                    type="checkbox"
                    checked={!!form.force_each}
                    onChange={(e) => setForm((f) => ({ ...f, force_each: e.target.checked }))}
                  />
                  Force as “each” (for non-measurable items like pack/roll/set)
                </label>

                {form.force_each && (
                  <div className="text-xs text-amber-300 mt-1">
                    Force as each will treat this item as count-based for recipe/replenishment. Use for “1 roll”, “1 pack”, “1 set”, “1m”, etc.
                  </div>
                )}
              </div>

              <Input
                type="number"
                step="0.01"
                label="Unit Price"
                value={form.unit_price}
                onChange={(v) => setForm((f) => ({ ...f, unit_price: v }))}
              />
              <Input
                type="number"
                label="Par Level"
                value={form.par_level}
                onChange={(v) => setForm((f) => ({ ...f, par_level: v }))}
              />
              <Input
                type="number"
                label="Reorder Frequency (days)"
                value={form.reorder_frequency}
                onChange={(v) => setForm((f) => ({ ...f, reorder_frequency: v }))}
              />
              <Input
                type="number"
                label="Current Stock"
                value={form.current_stock}
                onChange={(v) => setForm((f) => ({ ...f, current_stock: v }))}
              />
              <Input
                label="Category"
                value={form.category}
                onChange={(v) => setForm((f) => ({ ...f, category: v }))}
              />
              <Input
                type="number"
                label="Department ID"
                value={form.department_id}
                onChange={(v) => setForm((f) => ({ ...f, department_id: v }))}
              />

              <div className="md:col-span-2 flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-3 py-2 bg-[#161616] border border-gray-700 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  disabled={saving}
                  type="submit"
                  className="px-3 py-2 bg-emerald-600 hover:bg-emerald-700 rounded-lg inline-flex items-center gap-2 disabled:opacity-60"
                >
                  {saving ? <Loader className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Import */}
      {showImport && (
        <ImportItemsModal
          open={showImport}
          onClose={() => setShowImport(false)}
          onDone={load}
          vendorIdDefault={vendorIdFromURL}
        />
      )}
    </div>
  );
}

/* -------------------- helpers -------------------- */
const fmt = (n) => {
  if (n === null || n === undefined || n === '') return '—';
  const num = Number(n);
  return Number.isFinite(num) ? `£${num.toFixed(2)}` : '—';
};

const scrub = (obj) => {
  const out = { ...obj };

  // numeric coercion
  ['unit_price', 'par_level', 'current_stock', 'department_id', 'reorder_frequency'].forEach((k) => {
    if (out[k] === '') out[k] = null;
    else if (out[k] != null) out[k] = Number(out[k]);
  });

  // ✅ ensure boolean
  out.force_each = !!out.force_each;

  // normalize empty strings
  ['item_code', 'description', 'unit_size', 'category'].forEach((k) => {
    if (out[k] != null && typeof out[k] === 'string') out[k] = out[k].trim();
  });

  return out;
};

const Th = ({ children, className = '' }) => (
  <th className={`px-3 py-2 text-left font-semibold ${className}`}>{children}</th>
);
const Td = ({ children, className = '' }) => (
  <td className={`px-3 py-2 align-top ${className}`}>{children}</td>
);

const Input = ({ label, value, onChange, type = 'text', step, required }) => (
  <label className="flex flex-col gap-1">
    <span className="text-xs text-gray-400">
      {label}
      {required ? ' *' : ''}
    </span>
    <input
      type={type}
      step={step}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      className="bg-[#161616] border border-gray-700 rounded-lg px-3 py-2 outline-none"
    />
  </label>
);